import { z } from "zod";

// ============================================================================
// SAVINGS SCHEMAS
// ============================================================================

export const SummarySavingsOverallResponseSchema = z.object({
  total_savings: z.number(),
  total_autonomous_savings: z.number().default(0),
  total_assisted_savings: z.number().default(0),
  min_total_autonomous_savings: z.number().default(0),
  max_total_autonomous_savings: z.number().default(0),
  time_saved: z.string().default("0 days"),
  currency: z.string().default("USD"),
});

export type SummarySavingsOverallResponse = z.infer<typeof SummarySavingsOverallResponseSchema>;

export const SummarySavingsOverallEstimateResponseSchema = z.object({
  total_savings: z.number(),
  total_autonomous_savings: z.number().default(0),
  // DBU vs infrastructure split of the per-month autonomous savings.
  total_autonomous_dbu_savings: z.number().default(0),
  total_autonomous_infra_savings: z.number().default(0),
  total_assisted_savings: z.number().default(0),
  min_total_autonomous_savings: z.number().default(0),
  max_total_autonomous_savings: z.number().default(0),
  time_saved: z.string().default("0 days"),
  total_savings_estimate: z.number().default(0),
  total_autonomous_savings_estimate: z.number().default(0),
  total_assisted_savings_estimate: z.number().default(0),
  // DBU vs infrastructure split of the projected (annual) autonomous savings.
  total_autonomous_dbu_savings_estimate: z.number().default(0),
  total_autonomous_infra_savings_estimate: z.number().default(0),
  // DBU vs cloud-infrastructure split of the assisted savings (older backends omit it).
  total_assisted_dbu_savings: z.number().optional(),
  total_assisted_infra_savings: z.number().optional(),
  total_assisted_dbu_savings_estimate: z.number().optional(),
  total_assisted_infra_savings_estimate: z.number().optional(),
  total_time_saved_estimate: z.string().default("0 days"),
  currency: z.string().default("USD"),
});

export type SummarySavingsOverallEstimateResponse = z.infer<
  typeof SummarySavingsOverallEstimateResponseSchema
>;

// Per-day realized autonomous savings for the time-wise savings chart.
// `min_value`/`max_value` are the low/high of each day's estimate band, read
// straight from the mart's `min_*`/`max_*` columns server-side (a ~±10% band
// around `value`, not a fixed fraction).
export const SummarySavingsTimeseriesItemSchema = z.object({
  date: z.string(),
  value: z.number().default(0),
  min_value: z.number().default(0),
  max_value: z.number().default(0),
});

export const SummarySavingsTimeseriesResponseSchema = z.object({
  graph: z.array(SummarySavingsTimeseriesItemSchema).default([]),
  currency: z.string().default("USD"),
});

export type SummarySavingsTimeseriesItem = z.infer<typeof SummarySavingsTimeseriesItemSchema>;
export type SummarySavingsTimeseriesResponse = z.infer<
  typeof SummarySavingsTimeseriesResponseSchema
>;

// Assisted (potential-from-opportunities) savings scalars for the Assisted
// tab's metric cards. Matches the backend `AssistedSavingsResponse`.
export const SummarySavingsAssistedResponseSchema = z.object({
  total_assisted_savings: z.number().default(0),
  time_saved: z.string().default("0 days"),
  opportunities: z.number().default(0),
  currency: z.string().default("USD"),
});

export type SummarySavingsAssistedResponse = z.infer<typeof SummarySavingsAssistedResponseSchema>;

// ============================================================================
// API PARAMETER TYPES
// ============================================================================

export interface SummaryCostParams {
  start_date: string;
  end_date: string;
}

export interface SummarySavingsParams {
  start_date: string;
  end_date: string;
  savings_period?: string;
}

export interface SavingsData {
  total: number;
  autonomous: number;
  // Low/high bounds of the autonomous savings range for the tile hover.
  // Both 0 when there are no savings (tile then shows a plain number).
  autonomousMin?: number;
  autonomousMax?: number;
  assisted: number;
  timeSaved: string;
  // DBU vs infrastructure split of the autonomous savings (Future State only).
  autonomousDbu: number;
  autonomousInfra: number;
  // DBU vs infrastructure split of the assisted savings (Future State only).
  assistedDbu?: number;
  assistedInfra?: number;
}
